import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { clientFetch } from "../utils/clientFetch";

/**
 * 外部連携 APIキー操作履歴 (#469 / 管理画面 Phase C) のデータ取得フック。
 * apiKeyContext を雛形として踏襲。
 *
 * - 一覧は JSON envelope ({ data, pagination, status }) で取得。
 * - CSV (?format=csv) はサーバ生成の text/csv をそのまま Blob 化してダウンロードする
 *   (apiKeyExport の client 生成 CSV とは異なり、サーバ出力を素通しする)。
 */

const API_BASE = `${process.env.REACT_APP_API_ENDPOINT}/apis/admin`;

export type ApiKeyEventType = "issued" | "rotated" | "revoked" | "expires_extended" | "allowed_ips_changed";

export type ApiKeyEvent = {
  id: number;
  occurred_at: string;
  event_type: ApiKeyEventType;
  key_prefix: string | null;
  key_name: string | null;
  actor_admin_id: number | null;
  actor_kind: string | null;
  reason: string | null;
  metadata: Record<string, unknown> | null;
};

export type ApiKeyEventsPagination = { page: number; limit: number; total: number; total_pages: number };

export type ApiKeyEventsFilter = {
  api_key_id?: number | null;
  event_type?: ApiKeyEventType | null;
  from?: string | null;
  to?: string | null;
  page?: number;
  limit?: number;
};

export const EVENT_TYPE_LABELS: Record<ApiKeyEventType, string> = {
  issued: "発行",
  rotated: "再発行",
  revoked: "失効",
  expires_extended: "有効期限延長",
  allowed_ips_changed: "許可IP変更",
};

const DEFAULT_PAGINATION: ApiKeyEventsPagination = { page: 1, limit: 50, total: 0, total_pages: 0 };

/**
 * フィルタから events API 用クエリ文字列を組み立てる純関数 (テスト対象)。
 * companyId はパスに含むため対象外。空/未指定の項目は付与しない。
 */
export function buildEventsQuery(filter: ApiKeyEventsFilter): URLSearchParams {
  const q = new URLSearchParams();
  if (filter.api_key_id != null) q.set("api_key_id", String(filter.api_key_id));
  if (filter.event_type) q.set("event_type", filter.event_type);
  if (filter.from) q.set("from", filter.from);
  if (filter.to) q.set("to", filter.to);
  q.set("page", String(filter.page ?? 1));
  q.set("limit", String(filter.limit ?? 50));
  return q;
}

async function parseError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    return body?.message || `エラーが発生しました (${res.status})`;
  } catch {
    return `エラーが発生しました (${res.status})`;
  }
}

export function useApiKeyEventsContext() {
  const [events, setEvents] = useState<ApiKeyEvent[]>([]);
  const [pagination, setPagination] = useState<ApiKeyEventsPagination>(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState<boolean>(false);

  /** 操作履歴一覧取得 (フィルタ・ページング)。 */
  const refreshEvents = useCallback(async (companyId: number, filter: ApiKeyEventsFilter) => {
    setLoading(true);
    try {
      const q = buildEventsQuery(filter);
      const res = await clientFetch(
        `${API_BASE}/companies/${companyId}/external-api-key-events?${q.toString()}`,
        { method: "GET" }
      );
      if (!res.ok) {
        setEvents([]);
        setPagination(DEFAULT_PAGINATION);
        return;
      }
      const body = await res.json();
      setEvents(Array.isArray(body?.data) ? body.data : []);
      setPagination(body?.pagination ?? DEFAULT_PAGINATION);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 操作履歴 CSV (?format=csv) をダウンロードする。
   * サーバ生成 CSV を Blob 化し、一時 <a> でダウンロード保存する。
   * 成功で { ok:true }、失敗で { ok:false, message }。
   */
  const exportEventsCsv = useCallback(
    async (companyId: number, filter: ApiKeyEventsFilter): Promise<{ ok: boolean; message?: string }> => {
      const q = buildEventsQuery(filter);
      q.set("format", "csv");
      const res = await clientFetch(
        `${API_BASE}/companies/${companyId}/external-api-key-events?${q.toString()}`,
        { method: "GET" }
      );
      if (!res.ok) return { ok: false, message: await parseError(res) };
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `external-api-key-events-${companyId}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return { ok: true };
    },
    []
  );

  return useMemo(
    () => ({ events, pagination, loading, refreshEvents, exportEventsCsv }),
    [events, pagination, loading, refreshEvents, exportEventsCsv]
  );
}

type ApiKeyEventsContextValue = ReturnType<typeof useApiKeyEventsContext>;
const ApiKeyEventsContext = createContext<ApiKeyEventsContextValue | null>(null);

export function ApiKeyEventsProvider({ children }: { children: React.ReactNode }) {
  const value = useApiKeyEventsContext();
  return <ApiKeyEventsContext.Provider value={value}>{children}</ApiKeyEventsContext.Provider>;
}

export function useApiKeyEvents(): ApiKeyEventsContextValue {
  const ctx = useContext(ApiKeyEventsContext);
  if (!ctx) throw new Error("useApiKeyEvents must be used within ApiKeyEventsProvider");
  return ctx;
}
